import type { CardChoice, Ctx } from '@/core/context';
import type { Equipment } from '@/content/schema';
import { bus } from '@/core/bus';
import { bump } from './achievements';
import { tr } from '@/i18n';
import { basketLabel, cartChoices, cartSummary, toDirty } from './flow';

/**
 * 搬運（v0.4／v0.5）
 * - 烏龜車：可以「推」（E），空車也可以「拿起來」；推著時可以把籃子放上車一起推。烏龜車不能走樓梯（推的時候）。
 * - 籃子：只能「拿起來」，不能推；可以放到烏龜車、墊底籃架、桌子、架子、產線旁。不能直接放地上（廠規）。
 * - v0.5 籃子可以往上疊：
 *   放著的籃子上面可以再疊（on='bk:<下面那籃>'），一疊最多 carry.maxBaskets 籃；
 *   手上最多疊 HAND_MAX 籃一起搬（超過會擋住視線）；拿的時候從最上面拿。
 */
export const HAND_MAX = 3;

export function initCarts(ctx: Ctx): void {
  const d = ctx.day;
  d.carts = {}; d.baskets = {}; d.pushing = null; d.holding = null;
  for (const f of ctx.c.floors) {
    for (const c of f.carts) d.carts[c.id] = { floor: f.id, pos: [c.pos[0], c.pos[1]], load: null, baskets: [] };
    for (const b of f.baskets) {
      d.baskets[b.id] = { floor: f.id, pos: [b.pos[0], b.pos[1]], content: b.content, on: null };
      // v0.8：籃子下面墊著烏龜車
      if (b.cart && d.carts[b.cart]) { d.baskets[b.id].on = b.cart; d.baskets[b.id].pos = [...d.carts[b.cart].pos]; d.carts[b.cart].baskets.push(b.id); }
    }
  }
}

type Day = Ctx['day'];
/** 疊在 id 上面的那一籃 */
export const aboveOf = (d: Day, id: string) => Object.keys(d.baskets).find(k => d.baskets[k].on === `bk:${id}`) ?? null;
/** 從 id 往上整疊（含自己） */
export function stackFrom(d: Day, id: string): string[] {
  const out = [id]; let cur = aboveOf(d, id);
  while (cur && out.length < 20) { out.push(cur); cur = aboveOf(d, cur); }
  return out;
}
/** 往下找到這一疊最底下那籃 */
export function rootOf(d: Day, id: string): string {
  let cur = id; let n = 0;
  while (d.baskets[cur]?.on?.startsWith('bk:') && n++ < 20) cur = d.baskets[cur].on!.slice(3);
  return cur;
}
/** 手上拿的籃子（由下往上） */
export const handStack = (d: Day): string[] => d.holding?.kind === 'basket' ? stackFrom(d, d.holding.id) : [];

const cartCount = (ctx: Ctx, id: string) => { const c = ctx.day.carts[id]; return (c.load?.baskets ?? 0) + c.baskets.length; };
const ingName = (ctx: Ctx, content: string) => basketLabel(ctx.c, content);
const maxStack = (ctx: Ctx) => ctx.c.game.carry.maxBaskets;

/** E 在烏龜車旁：推／放開／拿起／拿下籃子 */
export function cartAction(ctx: Ctx, id: string): void {
  const d = ctx.day; const c = d.carts[id];
  if (d.pushing === id) { d.pushing = null; bus.emit('sfx', 'click'); ctx.ui.toast(tr("放開烏龜車。")); return done(ctx); }
  if (d.holding?.kind === 'basket') return putHandOnCart(ctx, id);
  if (d.holding) { ctx.ui.toast(tr("手上已經拿著東西了。")); return; }
  const n = cartCount(ctx, id);
  const choices: CardChoice[] = [
    { text: tr("🛒 推著走"), tone: 'primary', fn: () => { ctx.ui.close(); d.pushing = id; bus.emit('sfx', 'click');
      ctx.ui.toast(n ? tr("推著 {0} 籃。{1}", n, n > ctx.c.game.carry.warnAbove ? tr("疊得很高，轉彎慢一點、看前面。") : '') : tr("推起一台空的烏龜車。")); done(ctx); } },
  ];
  if (!n) choices.push({ text: tr("🙌 拿起來（空車才拿得動）"), fn: () => { ctx.ui.close(); d.holding = { kind: 'cart', id }; bus.emit('sfx', 'click'); ctx.ui.toast(tr("把空的烏龜車拿起來了。按 E 放下。")); done(ctx); } });
  if (c.baskets.length) choices.push({ text: tr("🧺 拿下最上面一籃（{0}）", ingName(ctx, d.baskets[c.baskets[c.baskets.length - 1]].content)), fn: () => {
    ctx.ui.close(); const bid = c.baskets.pop()!; d.baskets[bid].on = 'hand'; d.holding = { kind: 'basket', id: bid }; bus.emit('sfx', 'click'); done(ctx); } });
  choices.unshift(...cartChoices(ctx, id));
  choices.push({ text: tr("不用"), fn: () => ctx.ui.close() });
  const k = d.carts[id];
  ctx.ui.card({ name: k.tag === 'ship' ? tr("📦 成品車（{0}）", k.from ?? '') : tr("🛒 烏龜車"), role: cartSummary(ctx, id), color: '#3f78a8',
    text: k.tag === 'ship' ? (k.stage === 'arrived' ? tr("從成品電梯下來的成品。倉儲收貨簽收後拉出電梯，送到出貨區排車。") : tr("在出貨區排隊，等冷藏車裝車。"))
      : tr("烏龜車（平板推車）：食材、籃子不能直接放地上，線邊的籃子下面一定要有烏龜車。一台最多疊 {0} 籃，超過 4 籃矮個子不好搬、會擋住視線。\n推著一台車再對另一台按 E，可以把籃子收過來（例如收空籃、收髒籃一起推去洗）。", maxStack(ctx)), choices });
}

/** E 在籃子（一疊）旁：id 是這一疊最底下那籃 */
export function basketAction(ctx: Ctx, id: string): void {
  const d = ctx.day;
  const stack = stackFrom(d, id).filter(b => !d.baskets[b].used);
  const top = stack[stack.length - 1];
  if (d.holding?.kind === 'cart') { ctx.ui.toast(tr("手上拿著烏龜車，先放下。")); return; }
  if (d.pushing && !d.holding) {
    if (!top) return;
    if (cartCount(ctx, d.pushing) + 1 > maxStack(ctx)) { ctx.ui.toast(tr("🚫 車上最多 {0} 籃，放不下了。", maxStack(ctx))); return; }
    detach(d, top); return putOnCart(ctx, [top], d.pushing);
  }
  const hand = handStack(d);
  if (hand.length) {
    // 手上有籃子：疊上去（放下），或再拿一籃疊在手上
    const all = stackFrom(d, id);
    const choices: CardChoice[] = [];
    if (all.length + hand.length <= maxStack(ctx)) choices.push({ text: tr("⬆️ 疊上去（變成 {0} 籃一疊）", all.length + hand.length), tone: 'primary', fn: () => { ctx.ui.close(); stackHandOn(ctx, all[all.length - 1]); } });
    if (top && hand.length < HAND_MAX) choices.push({ text: tr("🙌 再拿最上面那籃（{0}）疊在手上", ingName(ctx, d.baskets[top].content)), fn: () => { ctx.ui.close(); liftOntoHand(ctx, top); } });
    if (!choices.length) { ctx.ui.toast(tr("🚫 疊太高了（最多 {0} 籃），手上也拿不下了。", maxStack(ctx))); return; }
    if (choices.length === 1) { choices[0].fn(); return; }
    choices.push({ text: tr("不用"), fn: () => ctx.ui.close() });
    ctx.ui.card({ name: tr("🧺 籃子"), role: tr("這一疊 {0} 籃・手上 {1} 籃", all.length, hand.length), color: '#2f6fb3', text: tr("籃子可以往上疊。疊太高會擋住視線、倒下來壓到腳：一疊最多 {0} 籃，手上最多疊 3 籃。", maxStack(ctx)), choices });
    return;
  }
  if (!top) { ctx.ui.toast(tr("這一疊已經在用了（線邊的料）。")); return; }
  const n = stack.length;
  if (n > 1) {
    const lift = stack.slice(-Math.min(HAND_MAX, n));
    ctx.ui.card({ name: tr("🧺 一疊籃子"), role: tr("{0} 籃", n), color: '#2f6fb3', text: tr("要拿幾籃？從最上面開始拿。"),
      choices: [
        { text: tr("🧺 拿最上面一籃（{0}）", ingName(ctx, d.baskets[top].content)), tone: 'primary', fn: () => { ctx.ui.close(); liftOntoHand(ctx, top); } },
        { text: tr("🙌 從上面拿 {0} 籃（疊著一起搬）", lift.length), fn: () => { ctx.ui.close(); liftOntoHand(ctx, lift[0]); } },
        { text: tr("不用"), fn: () => ctx.ui.close() },
      ] });
    return;
  }
  liftOntoHand(ctx, top);
}

/** 從 bid 開始（含上面疊的）拿到手上 */
function liftOntoHand(ctx: Ctx, bid: string): void {
  const d = ctx.day; const b = d.baskets[bid];
  const moving = stackFrom(d, bid);
  const hand = handStack(d);
  if (hand.length + moving.length > HAND_MAX) { ctx.ui.toast(tr("🙅 手上最多疊 {0} 籃，再多會擋住視線。", HAND_MAX)); return; }
  if (hand.length) b.on = `bk:${hand[hand.length - 1]}`;
  else { b.on = 'hand'; d.holding = { kind: 'basket', id: bid }; }
  for (const x of moving) d.baskets[x].floor = d.floor;
  bus.emit('sfx', 'click'); bump(ctx, 'basketsLifted');
  const total = hand.length + moving.length;
  if (total > 1) bump(ctx, 'basketsStacked');
  ctx.ui.toast(total > 1 ? tr("🧺 手上疊了 {0} 籃。慢慢走、看前面。", total) : tr("🧺 拿起一籃「{0}」。可以放到烏龜車上、疊到別的籃子上，或直接拿去目的地。", ingName(ctx, b.content)));
  done(ctx);
}

/** 把手上整疊放到某一籃（top）上面 */
function stackHandOn(ctx: Ctx, top: string): void {
  const d = ctx.day; const hand = handStack(d); if (!hand.length) return;
  const base = d.baskets[top];
  d.baskets[hand[0]].on = `bk:${top}`;
  for (const x of hand) { d.baskets[x].floor = base.floor; d.baskets[x].pos = [...base.pos]; }
  d.holding = null; bus.emit('sfx', 'click'); bump(ctx, 'basketsStacked');
  const n = stackFrom(d, rootOf(d, top)).length;
  ctx.ui.toast(tr("🧺 疊上去了（{0} 籃一疊）。{1}", n, n > ctx.c.game.carry.warnAbove ? tr("有點高了，小心不要撞到。") : ''));
  done(ctx);
}

/** 讓 bid 離開它所在的那一疊（上面的籃子往下接） */
function detach(d: Day, bid: string): void {
  const b = d.baskets[bid]; const up = aboveOf(d, bid);
  if (up) { d.baskets[up].on = b.on; d.baskets[up].pos = [...b.pos]; d.baskets[up].y = b.y; if (b.on === 'hand') d.holding = { kind: 'basket', id: up }; }
  else if (b.on === 'hand') d.holding = null;
  for (const c of Object.values(d.carts)) c.baskets = c.baskets.filter(x => x !== bid);
}

function putHandOnCart(ctx: Ctx, cartId: string): void {
  const hand = handStack(ctx.day);
  if (!hand.length) return;
  putOnCart(ctx, hand, cartId);
}
function putOnCart(ctx: Ctx, ids: string[], cartId: string): void {
  const d = ctx.day; const c = d.carts[cartId];
  if (cartCount(ctx, cartId) + ids.length > maxStack(ctx)) { ctx.ui.toast(tr("🚫 車上最多 {0} 籃，放不下了。", maxStack(ctx))); return; }
  for (const bid of ids) { c.baskets.push(bid); d.baskets[bid].on = cartId; d.baskets[bid].floor = c.floor; }
  if (d.holding?.kind === 'basket' && ids.includes(d.holding.id)) d.holding = null;
  bus.emit('sfx', 'click');
  const n = cartCount(ctx, cartId);
  ctx.ui.toast(tr("🧺 放上烏龜車（{0} 籃）。{1}", n, n > ctx.c.game.carry.warnAbove ? tr("超過 4 籃了：視線會被擋住，轉彎要慢。") : ''));
  done(ctx);
}

const PLACE_KINDS = new Set<Equipment['kind']>(['rack', 'table', 'shelf', 'conveyor', 'freezer', 'desk', 'machine', 'scale', 'labbench', 'cart']);
/** v0.8：放在「上面」的家具（桌子、架子）；其他（產線、機台）是線邊，籃子下面一定要有烏龜車 */
const ON_TOP = new Set<Equipment['kind']>(['rack', 'table', 'shelf', 'freezer', 'desk', 'scale', 'labbench', 'cart']);
/** 拿著東西按 E：放到旁邊的設備上；空地不行（籃子不能放地上），烏龜車可以放地上 */
export function putDown(ctx: Ctx, near: Equipment | null): void {
  const d = ctx.day; const h = d.holding; if (!h) return;
  const [x, z] = ctx.world.playerPos();
  if (h.kind === 'cart') {
    const c = d.carts[h.id]; c.floor = d.floor; c.pos = [x + 0.6, z + 0.6]; d.holding = null;
    bus.emit('sfx', 'click'); ctx.ui.toast(tr("放下烏龜車。")); return done(ctx);
  }
  const hand = handStack(d);
  if (!near || !PLACE_KINDS.has(near.kind)) {
    bump(ctx, 'basketFloorTry');
    ctx.ui.toast(tr("🙅 籃子不能直接放地上！放到烏龜車、墊底籃架、桌子、產線旁，或疊到別的籃子上。")); return;
  }
  if (!ON_TOP.has(near.kind)) {
    // 線邊：放到旁邊的烏龜車上；沒有車就不能放（食材不能直接放地上）
    const cart = Object.entries(d.carts).filter(([cid, c]) => cid !== d.pushing && c.floor === d.floor && Math.hypot(c.pos[0] - x, c.pos[1] - z) < 1.8 && cartCount(ctx, cid) + hand.length <= maxStack(ctx))
      .sort((a, b) => Math.hypot(a[1].pos[0] - x, a[1].pos[1] - z) - Math.hypot(b[1].pos[0] - x, b[1].pos[1] - z))[0];
    if (cart) return putOnCart(ctx, hand, cart[0]);
    if (d.pushing && cartCount(ctx, d.pushing) + hand.length <= maxStack(ctx)) return putOnCart(ctx, hand, d.pushing);
    bump(ctx, 'basketFloorTry');
    ctx.ui.toast(tr("🙅 線邊的籃子下面一定要有烏龜車！先推一台烏龜車過來（或放到旁邊的烏龜車上）。")); return;
  }
  // 桌子、架子：放在上面
  const pos: [number, number] = [Math.max(near.pos[0] - near.size[0] / 2 + 0.3, Math.min(x, near.pos[0] + near.size[0] / 2 - 0.3)),
    Math.max(near.pos[1] - near.size[2] / 2 + 0.22, Math.min(z, near.pos[1] + near.size[2] / 2 - 0.22))];
  const b = d.baskets[h.id];
  b.on = null; b.y = near.size[1] <= 1.2 ? near.size[1] : 0.9;
  for (const id of hand) { d.baskets[id].floor = d.floor; d.baskets[id].pos = [...pos]; }
  d.holding = null; bus.emit('sfx', 'click');
  ctx.ui.toast(tr("🧺 {0}放到「{1}」旁。", hand.length > 1 ? tr("{0} 籃一疊", hand.length) : '', near.name.split(tr("（"))[0].trim()));
  done(ctx);
}

/** 任務 carry：手上（整疊）或推著的車上有沒有這種籃子 */
export function basketHeldOrOnCart(ctx: Ctx, content: string): string | null {
  const d = ctx.day;
  const inHand = handStack(d).find(b => d.baskets[b].content === content);
  if (inHand) return inHand;
  if (d.pushing) { const id = d.carts[d.pushing].baskets.find(b => d.baskets[b].content === content); if (id) return id; }
  return null;
}
/** 送到了：籃子留在目的地旁邊（標記已使用，不能再拿）；疊在它上面的籃子往下接 */
export function consumeBasket(ctx: Ctx, bid: string, target: string): void {
  const d = ctx.day;
  detach(d, bid);
  // v0.8：料倒進去了，籃子變髒籃，放到這層樓的髒籃車（之後要推去洗籃區）
  void target; toDirty(ctx, bid);
}

export function releaseCartForStairs(ctx: Ctx): void {
  if (!ctx.day.pushing) return;
  ctx.day.pushing = null;
  ctx.ui.toast(tr("烏龜車推不上樓梯——物料請走貨梯。（空車可以拿起來帶走）"));
}
/** 換樓層：拿在手上的東西跟著走 */
export function carryAlong(ctx: Ctx): void {
  const d = ctx.day; const h = d.holding; if (!h) return;
  if (h.kind === 'basket') for (const b of handStack(d)) d.baskets[b].floor = d.floor; else d.carts[h.id].floor = d.floor;
}
function done(ctx: Ctx) { ctx.world.refreshActors(); ctx.save(); }

// ------------------------------------------------------------------ v0.6 貨架拿東西（放大檢視裡點一下）
export const SHELF_STOCK = { ingredient: 5, pack: 3 };
export function shelfLeft(ctx: Ctx, eqId: string, item: string): number {
  const isIng = ctx.c.ingredientById.has(item);
  return (isIng ? SHELF_STOCK.ingredient : SHELF_STOCK.pack) - (ctx.day.shelfTaken[`${eqId}:${item}`] ?? 0);
}
/** 拿一個：食材＝一籃拿在手上（疊在手上的籃子最上面），物品＝放進背包。回傳 null＝成功，字串＝不能拿的原因 */
export function takeFromShelf(ctx: Ctx, eqId: string, item: string): string | null {
  const d = ctx.day;
  if (shelfLeft(ctx, eqId, item) <= 0) return tr("架上沒有了。");
  const key = `${eqId}:${item}`;
  if (ctx.c.ingredientById.has(item)) {
    if (d.holding?.kind === 'cart') return tr("手上拿著烏龜車，先放下。");
    const hand = handStack(d);
    if (hand.length >= HAND_MAX) return tr("手上已經疊了 {0} 籃，拿不下了。", HAND_MAX);
    const id = `bk-take-${d.floor}-${eqId}-${item}-${(d.shelfTaken[key] ?? 0) + 1}`;
    const [x, z] = ctx.world.playerPos();
    d.baskets[id] = { floor: d.floor, pos: [x, z], content: item, on: hand.length ? `bk:${hand[hand.length - 1]}` : 'hand' };
    if (!hand.length) d.holding = { kind: 'basket', id };
    if (d.pushing && cartCount(ctx, d.pushing) >= maxStack(ctx)) { delete d.baskets[id]; if (d.holding?.id === id) d.holding = null; return tr("推車上放不下了。"); }
    if (d.pushing) { d.baskets[id].on = d.pushing; d.carts[d.pushing].baskets.push(id); if (d.holding?.id === id) d.holding = null; }
    bump(ctx, 'basketsLifted');
  } else {
    d.pack.push(item);
  }
  d.shelfTaken[key] = (d.shelfTaken[key] ?? 0) + 1;
  bus.emit('sfx', 'click');
  done(ctx);
  return null;
}

/** v1.1 放回貨架（冰箱拿錯了可以放回去）：手上最上面那籃／推車最上面那籃／背包裡的物品，只要是這個貨架有的東西就能放回 */
export function returnables(ctx: Ctx, eqId: string): { kind: 'hand' | 'cart' | 'pack'; item: string; basket?: string }[] {
  const d = ctx.day; const e = ctx.world.rt().equipment.get(eqId)?.data; const stock = e?.stock ?? [];
  const out: { kind: 'hand' | 'cart' | 'pack'; item: string; basket?: string }[] = [];
  const hand = handStack(d); const top = hand[hand.length - 1];
  if (top && stock.includes(d.baskets[top]?.content)) out.push({ kind: 'hand', item: d.baskets[top].content, basket: top });
  if (d.pushing) { const cb = d.carts[d.pushing].baskets; const ct = cb[cb.length - 1]; if (ct && stock.includes(d.baskets[ct]?.content)) out.push({ kind: 'cart', item: d.baskets[ct].content, basket: ct }); }
  for (const p of new Set(d.pack)) if (stock.includes(p)) out.push({ kind: 'pack', item: p });
  return out;
}
export function returnToShelf(ctx: Ctx, eqId: string, r: { kind: 'hand' | 'cart' | 'pack'; item: string; basket?: string }): void {
  const d = ctx.day; const key = `${eqId}:${r.item}`;
  if (r.kind === 'pack') { const i = d.pack.indexOf(r.item); if (i >= 0) d.pack.splice(i, 1); }
  else if (r.basket) {
    const b = d.baskets[r.basket];
    if (r.kind === 'hand') { if (d.holding?.id === r.basket) d.holding = null; }   // 疊在上面的籃子：直接拿掉最上面那籃
    else if (d.pushing) { const cb = d.carts[d.pushing].baskets; cb.splice(cb.indexOf(r.basket), 1); }
    void b;
    delete d.baskets[r.basket];
  }
  d.shelfTaken[key] = Math.max(0, (d.shelfTaken[key] ?? 0) - 1);
  bump(ctx, 'putBack'); bus.emit('sfx', 'click');
  done(ctx);
}
